import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Mail, Loader2, CheckCircle } from 'lucide-react';
import { z } from 'zod';

const emailSchema = z.string().trim().email({ message: "Please enter a valid email address" }).max(255);

export const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate email
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setError(result.error.errors[0].message);
      return;
    }

    setLoading(true);

    try {
      // Subscribe via Mailchimp (double opt-in)
      const { data, error: mailchimpError } = await supabase.functions.invoke('subscribe-mailchimp', {
        body: { email: result.data }
      });

      if (mailchimpError) {
        throw mailchimpError;
      }

      if (data?.alreadySubscribed) {
        toast({
          title: "Already subscribed!",
          description: "This email is already on our list.",
        });
      } else {
        toast({
          title: "Check your inbox!",
          description: "We've sent you a confirmation email.",
        });
      }
      setSuccess(true);
    } catch (err) {
      console.error('Newsletter signup error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-4">
        <CheckCircle className="w-10 h-10 mx-auto text-green-500 mb-3" />
        <h3 className="text-lg font-semibold text-foreground mb-1">Check your inbox!</h3>
        <p className="text-sm text-muted-foreground">
          Click the confirmation link to complete your subscription.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Mail className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-foreground">Stay Updated</h3>
          <p className="text-sm text-muted-foreground">Get notified about new car events</p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError('');
            }}
            className="h-10 rounded-lg border-border bg-background flex-1"
            disabled={loading}
          />
          <Button 
            type="submit" 
            className="h-10 px-6 rounded-lg font-medium"
            disabled={loading || !email}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Subscribe'
            )}
          </Button>
        </div>
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
        
        <p className="text-xs text-muted-foreground">
          No spam, unsubscribe anytime.
        </p>
      </form>
    </div>
  );
};
